import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import BudgetSitesLanding from "@/components/BudgetSitesLanding";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, PencilSimple, TrashSimple, MagnifyingGlass,
  TrendUp, TrendDown, Receipt, CalendarBlank, CurrencyCircleDollar,
  FunnelSimple, X, CaretLeft, Buildings, MapPin, CaretRight,
  FileXls, DownloadSimple, CheckCircle, WarningCircle, UploadSimple,
  CaretUp, CaretDown, ChartBar, CaretUpDown, Check,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";

// TR karakterleri normalize eden yardımcı — combobox filtresi için
const _normTR = (s) => (s || "").toString().toLowerCase()
  .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
  .replace(/İ/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");

// Aranabilir kategori seçici — SubPayments ile aynı desen (Faz 34.49)
function EntryCategoryCombobox({ value, onChange, options, testId }) {
  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(
    () => options.find((c) => c.code === value),
    [options, value],
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="mt-1 w-full flex items-center justify-between h-10 px-3 border border-slate-300 rounded-none sm:rounded-sm bg-white text-sm text-left hover:border-[#1B4776] focus:outline-none focus:border-[#1B4776]"
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[10px] text-[#1B4776] font-bold shrink-0">
                {selected.code}
              </span>
              <span className="text-xs truncate">{selected.name}</span>
            </span>
          ) : (
            <span className="text-slate-400">Kod veya isim yazın… (Örn: çimento, DGR.ISC)</span>
          )}
          <CaretUpDown size={14} className="opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[520px] max-w-[calc(100vw-2rem)]" align="start">
        <Command
          filter={(itemValue, search) => {
            const q = _normTR(search);
            const target = _normTR(itemValue);
            return target.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Kod veya isim yazın..."
                        data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Bütçe kalemi bulunamadı.</CommandEmpty>
            <CommandGroup>
              {options.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.name}`}
                  onSelect={() => { onChange(c.code); setOpen(false); }}
                  data-testid={`${testId}-item-${c.code}`}
                >
                  <Check size={14}
                         className={value === c.code ? "mr-2 text-[#1B4776]" : "opacity-0 mr-2"} />
                  <span className="font-mono text-[10px] text-[#1B4776] font-bold mr-2 shrink-0">
                    {c.code}
                  </span>
                  <span className="text-xs flex-1">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const emptyForm = {
  date: new Date().toISOString().slice(0, 10),
  site_id: "",
  category_code: "",
  description: "",
  amount_no_vat: "",
  amount_with_vat: "",
  vat_amount: "",
  other_taxes: "",
  tevkifat_rate: 0,
  status: "gercek",
  formality: "resmi",
  // Tedarikçi / Firma / Ekip — party_type: supplier | sub_firm | team | ""
  party_type: "",
  party_id: "",
  party_name: "",
  supplier_id: "",
  invoice_no: "",
  notes: "",
  payment_method: "",     // banka | nakit | cek | senet | kredi_karti (Faz 34.51)
  from_account: "",       // Kaynak hesap / IBAN / kart adı
};

// Ödeme yöntemi seçenekleri (Faz 34.51 — SubPayments ile aynı desen)
const PAYMENT_METHOD_OPTS = [
  { v: "banka",        label: "Banka",       long: "Banka / EFT" },
  { v: "nakit",        label: "Nakit",       long: "Nakit / Kasa" },
  { v: "cek",          label: "Çek",         long: "Çek" },
  { v: "senet",        label: "Senet",       long: "Senet" },
  { v: "kredi_karti",  label: "Kart",        long: "Kredi Kartı" },
];
const paymentMethodLabel = (v) =>
  (PAYMENT_METHOD_OPTS.find((m) => m.v === v)?.long) || "—";

const fmtTL = (n) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency", currency: "TRY", maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

// Sadece rakam/nokta/virgül karakterleri kabul edip TR bin ayracı ile formatla
// Girdi iki türde olabilir:
//   • Canonical (nokta = ondalık, örn. "144.00")  →  toFixed'ten gelen
//   • TR formatlı (nokta = binlik, virgül = ondalık, örn. "1.234,56")
// Bu iki durumu doğru ayırt eder.
const formatTR = (s) => {
  if (s === "" || s == null) return "";
  const str = String(s).trim();
  let intStr, decStr;
  if (/^-?\d+(\.\d+)?$/.test(str)) {
    // Canonical (tek nokta ondalık için)
    [intStr, decStr] = str.split(".");
  } else {
    const n = Number(str.replace(/\./g, "").replace(",", "."));
    if (!isFinite(n)) return "";
    [intStr, decStr] = String(n).split(".");
  }
  const withDots = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decStr ? `${withDots},${decStr}` : withDots;
};
const parseTR = (s) => String(s || "").replace(/\./g, "").replace(",", ".");

/**
 * Para input'u — TR bin ayracı (.) ile canlı format.
 * `value` canonical (nokta ondalık, "1234.56"); parent onChange'e canonical döner.
 */
function MoneyInput({ value, onChange, "data-testid": testId, ...rest }) {
  const [display, setDisplay] = React.useState(() => formatTR(value));
  const focusedRef = React.useRef(false);
  React.useEffect(() => {
    // Dışarıdan value değişirse ve odak yoksa senkronla
    if (!focusedRef.current) setDisplay(formatTR(value));
  }, [value]);
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d,.]/g, "");
    // Kullanıcının yazdıklarına dokunmadan canonical türet ve üste bildir
    const canonical = parseTR(raw);
    // Ekran: yazarken canlı bin ayracı ile formatla (odak sırasında)
    // Ondalık kısım yazılıyorsa (virgülden sonra), sonundaki virgülü koru
    const hasTrailingComma = raw.endsWith(",");
    const decimalHalf = raw.match(/,(\d*)$/);
    const intPart = canonical.split(".")[0] || "";
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    let out = intFormatted;
    if (hasTrailingComma) out += ",";
    else if (decimalHalf && decimalHalf[1] !== undefined && raw.includes(",")) {
      out = intFormatted + "," + decimalHalf[1];
    }
    setDisplay(out);
    onChange(canonical);
  };
  return (
    <Input type="text" inputMode="decimal" value={display}
           onFocus={() => { focusedRef.current = true; }}
           onBlur={() => { focusedRef.current = false; setDisplay(formatTR(value)); }}
           onChange={handleChange}
           data-testid={testId}
           {...rest} />
  );
}


/**
 * Ortak Gelir/Gider giriş sayfası. `kind` prop'una göre filtreleyerek çalışır.
 */

// Kayıt Türü etiket ve renk yardımcıları (Faz 34.48 — Vergi/Harç eklendi)
const FORMALITY_META = {
  resmi:       { label: "Resmi",       long: "Resmi Kayıt",  color: "emerald", short: "✓ Resmi",       help: "Muhasebe programında kayıtlı (giderleştirilebilen)" },
  gayri_resmi: { label: "Gayri Resmi", long: "Gayri Resmi",  color: "amber",   short: "⚠ Gayri Resmi", help: "Muhasebeye yansımayan (elden ödeme vb.)" },
  vergi_harc:  { label: "Vergi/Harç",  long: "Vergi / Harç", color: "sky",     short: "⚖ Vergi/Harç",  help: "Vergi, harç, resmî ödemeler (KDV'siz tek tutar)" },
};
const formalityLabel = (f) => FORMALITY_META[f]?.label || FORMALITY_META.resmi.label;

export default function BudgetEntriesPage({ kind, title, subtitle, testIdPrefix }) {
  const { sites, activeSiteId, setActiveSiteId } = useSites();
  const [rows, setRows] = useState([]);
  const [siteSummary, setSiteSummary] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [subFirms, setSubFirms] = useState([]);
  const [teams, setTeams] = useState([]);
  // Faz 34.55: Ödeme hesapları (banka + kredi kartı)
  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formalityFilter, setFormalityFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Sıralama + tedarikçi grafiği filtresi
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [partyFilter, setPartyFilter] = useState("");
  // Grafik grup boyutu — varsayılan CBS gider kalemleri (kategori)
  // 'category' | 'party' | 'formality' | 'month'
  const [chartGroupBy, setChartGroupBy] = useState("category");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const confirm = useConfirm();

  const currentSiteId = activeSiteId && activeSiteId !== "all" ? activeSiteId : "";
  const kindLabel = kind === "gelir" ? "Gelir" : "Gider";
  const kindColor = kind === "gelir" ? "emerald" : "rose";

  const load = async () => {
    try {
      const params = { kind };
      if (currentSiteId) params.site_id = currentSiteId;
      if (categoryFilter !== "all") params.category_code = categoryFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (formalityFilter !== "all") params.formality = formalityFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/budget/entries`, { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadCategories = async () => {
    try {
      const params = { kind, include_system: true };
      if (currentSiteId) params.site_id = currentSiteId;
      const { data } = await axios.get(`${API}/budget/categories`, { params });
      setCategories(data);
    } catch (e) { /* ignore */ }
  };

  const loadSuppliers = async () => {
    try {
      const { data } = await axios.get(`${API}/suppliers`);
      setSuppliers(data);
    } catch (e) { /* ignore */ }
    try {
      const { data } = await axios.get(`${API}/subcontractors`);
      setSubFirms(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) { /* ignore */ }
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeams(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) { /* ignore */ }
  };

  const loadSiteSummary = async () => {
    try {
      // Both gelir + gider totals döner — landing kartlarında iki rakamı birden gösteriyoruz
      const { data } = await axios.get(`${API}/budget/site-summary`,
        { params: { include_kapali: true } });
      setSiteSummary(data);
    } catch (e) { /* ignore */ }
  };

  // Site kartları görünümü: gelir VEYA gider sayfasında + "tüm şantiyeler" seçili iken
  const showSiteLanding = (kind === "gelir" || kind === "gider") && !currentSiteId;

  useEffect(() => { load(); }, [currentSiteId, categoryFilter, statusFilter, formalityFilter, dateFrom, dateTo, kind]); // eslint-disable-line
  useEffect(() => { loadCategories(); }, [currentSiteId, kind]); // eslint-disable-line
  useEffect(() => { loadSuppliers(); }, []);
  // Faz 34.55: Aktif banka hesaplarını ve kredi kartlarını yükle
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/accounts`, { params: { active_only: true } });
        setBankAccounts(data.filter((a) => a.type === "bank_account"));
        setCreditCards(data.filter((a) => a.type === "credit_card"));
      } catch {}
    })();
  }, []);
  useEffect(() => { if (showSiteLanding) loadSiteSummary(); }, [showSiteLanding]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // KDV Hariç + KDV Bedeli + Diğer Vergiler = KDV Dahil (Faz 34.48c)
  const _recalcWithVat = (noVat, vatAmt, otherTx) => {
    const n = parseFloat(noVat) || 0;
    const v = parseFloat(vatAmt) || 0;
    const o = parseFloat(otherTx) || 0;
    return (n + v + o);
  };
  const onChangeNoVat = (v) => {
    setForm((f) => {
      const withVat = _recalcWithVat(v, f.vat_amount, f.other_taxes);
      return { ...f, amount_no_vat: v, amount_with_vat: withVat ? withVat.toFixed(2) : "" };
    });
  };
  const onChangeVatAmount = (v) => {
    setForm((f) => {
      const withVat = _recalcWithVat(f.amount_no_vat, v, f.other_taxes);
      return { ...f, vat_amount: v, amount_with_vat: withVat ? withVat.toFixed(2) : "" };
    });
  };
  const onChangeOtherTaxes = (v) => {
    setForm((f) => {
      const withVat = _recalcWithVat(f.amount_no_vat, f.vat_amount, v);
      return { ...f, other_taxes: v, amount_with_vat: withVat ? withVat.toFixed(2) : "" };
    });
  };
  const onChangeWithVat = (v) => {
    // Manuel yazılınca sadece toplamı güncelle — diğer alanlara dokunma
    setForm((f) => ({ ...f, amount_with_vat: v }));
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      site_id: currentSiteId || (sites[0]?.id || ""),
      category_code: categoryFilter !== "all" ? categoryFilter : "",
    });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date || "",
      site_id: r.site_id || "",
      category_code: r.category_code || "",
      description: r.description || "",
      amount_no_vat: r.amount_no_vat ?? "",
      amount_with_vat: r.amount_with_vat ?? "",
      vat_amount: r.vat_amount ?? "",
      other_taxes: r.other_taxes ?? "",
      tevkifat_rate: r.tevkifat_rate ?? 0,
      status: r.status || "gercek",
      formality: r.formality || "resmi",
      supplier_id: r.supplier_id || "",
      party_type: r.party_type || (r.supplier_id ? "supplier" : ""),
      party_id: r.party_id || r.supplier_id || "",
      party_name: r.party_name || "",
      invoice_no: r.invoice_no || "",
      notes: r.notes || "",
      payment_method: r.payment_method || "",
      from_account: r.from_account || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.site_id) { toast.error("Şantiye seçin"); return; }
      if (!form.category_code) { toast.error("Kategori seçin"); return; }
      if (!form.amount_no_vat) { toast.error("Tutar girin"); return; }
      const amountNoVat = parseFloat(form.amount_no_vat) || 0;
      const isResmi = (form.formality || "resmi") === "resmi";
      // Resmi kayıt: KDV Bedeli + Diğer Vergiler doğrudan tutar (Faz 34.48c)
      const vatAmount = isResmi ? (parseFloat(form.vat_amount) || 0) : 0;
      const otherTaxes = isResmi ? (parseFloat(form.other_taxes) || 0) : 0;
      const amountWithVat = isResmi
        ? (parseFloat(form.amount_with_vat) || (amountNoVat + vatAmount + otherTaxes))
        : amountNoVat;
      const payload = {
        date: form.date,
        site_id: form.site_id,
        category_code: form.category_code,
        kind,
        description: form.description,
        amount_no_vat: amountNoVat,
        amount_with_vat: amountWithVat,
        vat_amount: vatAmount,
        other_taxes: otherTaxes,
        tevkifat_rate: 0,
        status: form.status,
        formality: form.formality || "resmi",
        // Tedarikçi / Firma / Ekip
        party_type: form.party_type || "",
        party_id: form.party_id || "",
        party_name: form.party_name || "",
        supplier_id: form.party_type === "supplier" ? (form.party_id || "") : "",
        // Sadece resmi kayıtta fatura no anlamlı
        invoice_no: isResmi ? form.invoice_no : "",
        notes: form.notes,
        // Ödeme hesabı (Faz 34.51)
        payment_method: form.payment_method || "",
        from_account: form.from_account || "",
      };
      if (editing) {
        await axios.put(`${API}/budget/entries/${editing.id}`, payload);
        toast.success(`${kindLabel} kaydı güncellendi`);
      } else {
        await axios.post(`${API}/budget/entries`, payload);
        toast.success(`${kindLabel} kaydı eklendi`);
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    const cat = categories.find((c) => c.code === r.category_code);
    const ok = await confirm({
      title: `${kindLabel} Kaydını Sil`,
      description: `${fmtDate(r.date)} — ${cat?.name || r.category_code} — ${fmtTL(r.amount_with_vat)} kalıcı olarak silinsin mi?`,
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/budget/entries/${r.id}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Metin araması (frontend'de) + kategori adı ile enrich
  const filtered = useMemo(() => {
    const catMap = Object.fromEntries(categories.map((c) => [c.code, c.name]));
    const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));
    let out = rows.map((r) => ({
      ...r,
      category_name: catMap[r.category_code] || r.category_code,
      // Tedarikçi görünen ad: önce sistem eşleşmesi (supplier_id), sonra
      // Excel'den gelen serbest metin (party_name).
      supplier_name:
        (r.supplier_id && supMap[r.supplier_id]) ||
        r.party_name ||
        (r.supplier_id ? "—" : ""),
      site_name: sites.find((s) => s.id === r.site_id)?.name || "",
    }));
    if (q) {
      const nq = q.toLowerCase();
      out = out.filter((r) =>
        (r.description || "").toLowerCase().includes(nq) ||
        (r.category_name || "").toLowerCase().includes(nq) ||
        (r.category_code || "").toLowerCase().includes(nq) ||
        (r.invoice_no || "").toLowerCase().includes(nq) ||
        (r.supplier_name || "").toLowerCase().includes(nq)
      );
    }
    // Tedarikçi filtresi (grafikten seçim)
    if (partyFilter) {
      out = out.filter((r) => (r.supplier_name || "") === partyFilter);
    }
    // Sıralama
    if (sortBy) {
      const dir = sortDir === "asc" ? 1 : -1;
      const getVal = (r) => {
        if (sortBy === "payment_channel") {
          return `${paymentMethodLabel(r.payment_method || "")} ${r.from_account || ""}`.trim();
        }
        return r[sortBy];
      };
      out = [...out].sort((a, b) => {
        const av = getVal(a);
        const bv = getVal(b);
        if (av == null && bv == null) return 0;
        if (av == null || av === "") return 1;
        if (bv == null || bv === "") return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv), "tr") * dir;
      });
    }
    return out;
  }, [rows, categories, suppliers, sites, q, sortBy, sortDir, partyFilter]);

  const totals = useMemo(() => {
    const noVat = filtered.reduce((a, r) => a + (Number(r.amount_no_vat) || 0), 0);
    const withVat = filtered.reduce((a, r) => a + (Number(r.amount_with_vat) || 0), 0);
    return { noVat, withVat, kdv: withVat - noVat, count: filtered.length };
  }, [filtered]);

  // Dağılım grafiği — grup boyutu 'chartGroupBy'a göre değişir.
  //   'category'  → CBS gider kalemleri (varsayılan)
  //   'party'     → Tedarikçi / Firma / Ekip
  //   'formality' → Resmi / Gayri Resmi
  //   'month'     → Aylık (YYYY-MM)
  const partyDistribution = useMemo(() => {
    const catMap = Object.fromEntries(categories.map((c) => [c.code, c.name]));
    const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));
    const keyOf = (r) => {
      switch (chartGroupBy) {
        case "party":
          return (r.supplier_id && supMap[r.supplier_id]) ||
                 r.party_name || "(Atanmamış)";
        case "formality":
          return formalityLabel(r.formality);
        case "month":
          return (r.date || "").slice(0, 7) || "(Tarihsiz)";
        case "category":
        default:
          if (!r.category_code) return "(Atanmamış)";
          return `${r.category_code} — ${catMap[r.category_code] || r.category_code}`;
      }
    };
    const bucket = new Map();
    for (const r of rows) {
      const k = keyOf(r);
      const cur = bucket.get(k) || { name: k, value: 0, count: 0 };
      cur.value += Number(r.amount_with_vat) || 0;
      cur.count += 1;
      bucket.set(k, cur);
    }
    let arr = Array.from(bucket.values());
    if (chartGroupBy === "month") {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      arr.sort((a, b) => b.value - a.value);
    }
    // Grafik okunur kalsın: en yüksek 12 (aylık için tümü)
    return chartGroupBy === "month" ? arr : arr.slice(0, 12);
  }, [rows, suppliers, categories, chartGroupBy]);

  const chartGroupLabel = useMemo(() => ({
    category: "CBS Gider Kalemleri — En Yüksek 12",
    party: "Tedarikçi / Firma / Ekip — En Yüksek 12",
    formality: "Kayıt Türü (Resmi / Gayri Resmi)",
    month: "Aylık Dağılım",
  }[chartGroupBy] || "Dağılım"), [chartGroupBy]);

  // Grafik'ten seçim → tabloyu süz. Kategori/formality/tarih bazlıda tablonun
  // ilgili filtresi ayarlanır, party bazlıda `partyFilter` state'i kullanılır.
  const applyChartClick = (name) => {
    if (chartGroupBy === "party") {
      setPartyFilter((cur) => (cur === name ? "" : name));
      return;
    }
    if (chartGroupBy === "category") {
      const code = String(name).split(" — ")[0];
      setCategoryFilter((cur) => (cur === code ? "all" : code));
      return;
    }
    if (chartGroupBy === "formality") {
      const inv = Object.entries(FORMALITY_META).find(([, m]) => m.label === name);
      if (!inv) return;
      const val = inv[0];
      setFormalityFilter((cur) => (cur === val ? "all" : val));
      return;
    }
    if (chartGroupBy === "month") {
      // Ayı seçince tarih aralığı filtresi
      const [y, m] = name.split("-").map(Number);
      if (!y || !m) return;
      const from = `${y}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const to = `${y}-${String(m).padStart(2, "0")}-${lastDay}`;
      setDateFrom((cur) => cur === from ? "" : from);
      setDateTo((cur) => cur === to ? "" : to);
    }
  };
  const chartActiveKey = useMemo(() => {
    if (chartGroupBy === "party") return partyFilter;
    if (chartGroupBy === "category" && categoryFilter !== "all") {
      const cat = categories.find((c) => c.code === categoryFilter);
      return `${categoryFilter} — ${cat?.name || categoryFilter}`;
    }
    if (chartGroupBy === "formality" && formalityFilter !== "all") {
      return formalityLabel(formalityFilter);
    }
    return "";
  }, [chartGroupBy, partyFilter, categoryFilter, formalityFilter, categories]);

  // Sıralama tıklama işleyicisi
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col); setSortDir("asc");
    }
  };
  const SortableTh = ({ col, className, children, testId }) => (
    <th
      className={`${className || ""} cursor-pointer select-none hover:text-[#1B4776] transition-colors`}
      onClick={() => toggleSort(col)}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === col && (
          sortDir === "asc"
            ? <CaretUp size={10} weight="fill" />
            : <CaretDown size={10} weight="fill" />
        )}
      </span>
    </th>
  );

  // Sadece "leaf" kategoriler (giriş için) — üstlerine kayıt girmemek daha temiz
  const leafCategories = useMemo(() => {
    const parentCodes = new Set(categories.map((c) => c.parent_code).filter(Boolean));
    return categories.filter((c) => !parentCodes.has(c.code));
  }, [categories]);

  return (
    <Shell title={title} subtitle={subtitle}>
      {/* Şantiye Karşılama — CBS-tarzı ortak kart tasarımı (Faz 34.55) */}
      {showSiteLanding && (
        <BudgetSitesLanding
          title={title}
          subtitle={`${kindLabel} kayıtlarını yönetmek için önce bir şantiye seçin.`}
          icon={kind === "gelir" ? TrendUp : TrendDown}
          accent={kind === "gelir" ? "emerald" : "rose"}
          onSelectSite={(sid) => setActiveSiteId(sid)}
          testIdPrefix={testIdPrefix}
        />
      )}
      {/* Şantiye seçildiğinde: geri dön butonu */}
      {(kind === "gelir" || kind === "gider") && currentSiteId && (
        <div className="mb-3">
          <button
            onClick={() => setActiveSiteId("all")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B4776] hover:text-[#95C11F] transition-colors"
            data-testid="entries-back-to-sites"
          >
            <CaretLeft size={14} weight="bold" />
            Tüm Şantiyelere Dön
          </button>
        </div>
      )}
      {!showSiteLanding && (
        <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Kayıt Sayısı</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{totals.count}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam KDV Hariç</div>
          <div className={`text-lg font-bold text-${kindColor}-700 mt-1`}>{fmtTL(totals.noVat)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam KDV Dahil</div>
          <div className={`text-lg font-bold text-${kindColor}-800 mt-1`}>{fmtTL(totals.withVat)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">KDV Toplamı</div>
          <div className="text-lg font-bold text-slate-600 mt-1">{fmtTL(totals.kdv)}</div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3 flex-1 items-center">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid={`${testIdPrefix}-search`}
                placeholder="Açıklama, kategori, fatura no, tedarikçi..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 rounded-none sm:rounded-sm"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-56 rounded-none sm:rounded-sm" data-testid={`${testIdPrefix}-category-filter`}>
                <SelectValue placeholder="Tüm kategoriler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {leafCategories.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="font-mono text-xs text-[#1B4776] mr-2">{c.code}</span>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 rounded-none sm:rounded-sm" data-testid={`${testIdPrefix}-status-filter`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="gercek">Gerçekleşen</SelectItem>
                <SelectItem value="tahmin">Tahmin (Plan)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={formalityFilter} onValueChange={setFormalityFilter}>
              <SelectTrigger className="w-40 rounded-none sm:rounded-sm" data-testid={`${testIdPrefix}-formality-filter`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="resmi">Sadece Resmi</SelectItem>
                <SelectItem value="gayri_resmi">Sadece Gayri Resmi</SelectItem>
                {kind === "gider" && (
                  <SelectItem value="vergi_harc">Sadece Vergi/Harç</SelectItem>
                )}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                     className="rounded-none sm:rounded-sm w-40" data-testid={`${testIdPrefix}-date-from`} />
              <span className="text-slate-400">—</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                     className="rounded-none sm:rounded-sm w-40" data-testid={`${testIdPrefix}-date-to`} />
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                        className="text-slate-400 hover:text-rose-600 p-1">
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {kind === "gider" && (
              <Button
                onClick={() => setImportOpen(true)}
                variant="outline"
                className="rounded-none sm:rounded-sm whitespace-nowrap border-[#95C11F] text-[#556B10] hover:bg-[#F7FBE9]"
                data-testid="entry-import-btn"
              >
                <FileXls size={14} weight="bold" className="mr-1.5" />
                Excel'den İçe Aktar
              </Button>
            )}
            <Button
              onClick={openNew}
              className={`bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm whitespace-nowrap`}
              data-testid={`${testIdPrefix}-add-btn`}
            >
              <Plus size={14} weight="bold" className="mr-1.5" />
              Yeni {kindLabel} Kaydı
            </Button>
          </div>
        </div>
      </Card>

      {/* Dinamik Dağılım Grafiği (yalnızca gider) */}
      {kind === "gider" && currentSiteId && partyDistribution.length > 0 && (
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200"
              data-testid={`${testIdPrefix}-party-chart`}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ChartBar size={16} weight="duotone" className="text-[#95C11F]" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">
                Dağılım (KDV Dahil) — {chartGroupLabel}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {[
                { k: "category",  label: "Kategori (CBS)", tid: "chart-group-category" },
                { k: "party",     label: "Tedarikçi/Firma/Ekip", tid: "chart-group-party" },
                { k: "formality", label: "Kayıt Türü", tid: "chart-group-formality" },
                { k: "month",     label: "Aylık", tid: "chart-group-month" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setChartGroupBy(o.k)}
                  data-testid={`${testIdPrefix}-${o.tid}`}
                  className={`px-2.5 py-1 text-[11px] font-bold border transition-colors ${
                    chartGroupBy === o.k
                      ? "bg-[#1B4776] text-white border-[#1B4776]"
                      : "bg-white text-[#1B4776] border-slate-300 hover:border-[#1B4776]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {chartActiveKey && (
              <button
                type="button"
                onClick={() => {
                  setPartyFilter("");
                  setCategoryFilter("all");
                  setFormalityFilter("all");
                  setDateFrom(""); setDateTo("");
                }}
                data-testid={`${testIdPrefix}-clear-chart-filter`}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1B4776] bg-slate-100 hover:bg-slate-200 px-2 py-1 transition-colors"
              >
                <X size={10} weight="bold" />
                Filtreyi kaldır: {chartActiveKey}
              </button>
            )}
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={partyDistribution} margin={{ top: 10, right: 20, left: 0, bottom: 70 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                  height={80}
                  tick={{ fontSize: 10, fill: "#475569" }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#475569" }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)}
                />
                <Tooltip
                  formatter={(v, n, p) => [fmtTL(v), `${p.payload.count} kayıt`]}
                  labelStyle={{ color: "#1B4776", fontWeight: 700, fontSize: 12 }}
                  contentStyle={{ fontSize: 11, borderRadius: 0, border: "1px solid #cbd5e1" }}
                />
                <Bar
                  dataKey="value"
                  cursor="pointer"
                  onClick={(d) => applyChartClick(d.name)}
                >
                  {partyDistribution.map((d, i) => {
                    // Aktif filtre varsa seçili bar lacivert, seçilmeyenler gri.
                    // Aktif filtre yoksa her bara farklı renk (canlı palet).
                    const CHART_COLORS = [
                      "#95C11F", "#1B4776", "#EF4444", "#F59E0B", "#10B981",
                      "#8B5CF6", "#EC4899", "#06B6D4", "#F97316", "#84CC16",
                      "#6366F1", "#14B8A6",
                    ];
                    const fill = chartActiveKey
                      ? (chartActiveKey === d.name ? "#1B4776" : "#cbd5e1")
                      : CHART_COLORS[i % CHART_COLORS.length];
                    return <Cell key={i} fill={fill} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Bir sütuna tıklayarak sadece o gruba ait kayıtları süzebilirsiniz.
            Varsayılan boyut CBS gider kalemleridir.
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh col="date" className="px-4 py-3 text-left w-28" testId={`${testIdPrefix}-sort-date`}>Tarih</SortableTh>
                <SortableTh col="category_code" className="px-4 py-3 text-left w-48" testId={`${testIdPrefix}-sort-cat`}>Kategori</SortableTh>
                <SortableTh col="description" className="px-4 py-3 text-left" testId={`${testIdPrefix}-sort-desc`}>Açıklama</SortableTh>
                {!currentSiteId && (
                  <SortableTh col="site_name" className="px-4 py-3 text-left w-32" testId={`${testIdPrefix}-sort-site`}>Şantiye</SortableTh>
                )}
                <SortableTh col="supplier_name" className="px-4 py-3 text-left w-40" testId={`${testIdPrefix}-sort-sup`}>Tedarikçi / Firma / Ekip</SortableTh>
                <SortableTh col="invoice_no" className="px-4 py-3 text-left w-28" testId={`${testIdPrefix}-sort-inv`}>Fatura No</SortableTh>
                <SortableTh col="payment_channel" className="px-4 py-3 text-left w-40" testId={`${testIdPrefix}-sort-pay`}>Ödeme Kanalı</SortableTh>
                <SortableTh col="amount_no_vat" className="px-4 py-3 text-right w-32" testId={`${testIdPrefix}-sort-novat`}>KDV Hariç</SortableTh>
                <SortableTh col="amount_with_vat" className="px-4 py-3 text-right w-32" testId={`${testIdPrefix}-sort-withvat`}>KDV Dahil</SortableTh>
                <SortableTh col="status" className="px-4 py-3 text-center w-24" testId={`${testIdPrefix}-sort-status`}>Durum</SortableTh>
                <SortableTh col="formality" className="px-4 py-3 text-center w-24" testId={`${testIdPrefix}-sort-form`}>Kayıt</SortableTh>
                <th className="px-4 py-3 text-right w-24">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid={`${testIdPrefix}-table-body`}>
              {filtered.length === 0 && (
                <tr>
                    <td colSpan={11} className="px-4 py-16 text-center text-slate-400">
                    {kind === "gelir"
                      ? <TrendUp size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
                      : <TrendDown size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />}
                    <p className="text-sm">Henüz kayıt yok — "Yeni {kindLabel} Kaydı" ile başlayın.</p>
                  </td>
                </tr>
              )}
              {filtered.map((r, i) => (
                <tr key={r.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${
                      r.formality === "gayri_resmi"
                        ? "bg-amber-50/70 hover:bg-amber-100/60"
                        : r.formality === "vergi_harc"
                        ? "bg-sky-50/70 hover:bg-sky-100/60"
                        : (i % 2 ? "bg-slate-50/50" : "")
                    }`}
                    data-testid={`${testIdPrefix}-row-${i}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-start gap-1.5">
                      {r.needs_category && (
                        <span
                          title="Gider uygun bütçe kalemine atanmadı — Düzenle ile bir kod seçin"
                          className="inline-flex items-center text-rose-600 mt-0.5"
                          data-testid={`${testIdPrefix}-warn-${i}`}>
                          <WarningCircle size={16} weight="fill" />
                        </span>
                      )}
                      {(r.import_warnings || []).length > 0 && (
                        <span
                          title={"İçe aktarma uyarıları:\n- " + (r.import_warnings || []).join("\n- ")}
                          className="inline-flex items-center text-amber-600 mt-0.5"
                          data-testid={`${testIdPrefix}-import-warn-${i}`}>
                          <WarningCircle size={16} weight="fill" />
                        </span>
                      )}
                      <div>
                        {r.category_code ? (
                          <>
                            <div className="font-mono text-[10px] text-[#1B4776] font-bold">{r.category_code}</div>
                            <div className="text-xs text-slate-700">{r.category_name}</div>
                          </>
                        ) : (
                          <div className="text-[11px] font-bold text-rose-600 uppercase tracking-widest">
                            Atanmamış
                          </div>
                        )}
                        {(r.import_warnings || []).length > 0 && (
                          <div className="text-[9px] text-amber-700 italic mt-0.5">
                            İçe aktarma uyarısı: {(r.import_warnings || []).slice(0, 2).join(" · ")}
                            {(r.import_warnings || []).length > 2 ? "…" : ""}
                          </div>
                        )}
                        {r.source === "sub_payment" && (
                          <div className="text-[9px] text-slate-400 italic mt-0.5">
                            Taşeron ödemesinden otomatik
                          </div>
                        )}
                        {r.source === "salary_payment_daily" && (
                          <div className="text-[9px] text-slate-400 italic mt-0.5">
                            Maaş ödemeleri (günlük kümülatif) — otomatik
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {r.description || <span className="text-slate-400">—</span>}
                    {r.notes && <div className="text-[10px] text-slate-400 mt-0.5">{r.notes}</div>}
                  </td>
                  {!currentSiteId && (
                    <td className="px-4 py-2.5 text-xs text-slate-600">{r.site_name || "—"}</td>
                  )}
                  <td className="px-4 py-2.5 text-xs text-slate-600">{r.supplier_name || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{r.invoice_no || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {(r.payment_method || r.from_account) ? (
                      <div className="leading-tight">
                        {r.payment_method && (
                          <div>
                            <span className="inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border bg-slate-50 text-slate-700 border-slate-200">
                              {paymentMethodLabel(r.payment_method)}
                            </span>
                          </div>
                        )}
                        {r.from_account && (
                          <div className="text-[10px] font-mono text-slate-600 mt-0.5 truncate max-w-[200px]"
                               title={r.from_account}>
                            {r.from_account}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono text-xs text-${kindColor}-700`}>
                    {fmtTL(r.amount_no_vat)}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono text-xs font-bold text-${kindColor}-800`}>
                    {fmtTL(r.amount_with_vat)}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                      r.status === "tahmin"
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-slate-50 text-slate-700 border-slate-200"
                    }`}>
                      {r.status === "tahmin" ? "Tahmin" : "Gerçek"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                      r.formality === "gayri_resmi"
                        ? "bg-amber-100 text-amber-800 border-amber-400"
                        : r.formality === "vergi_harc"
                        ? "bg-sky-100 text-sky-800 border-sky-400"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`} title={FORMALITY_META[r.formality]?.help || FORMALITY_META.resmi.help}>
                      {formalityLabel(r.formality)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(r)} className="p-1 text-slate-400 hover:text-[#1B4776]"
                              title="Düzenle" data-testid={`${testIdPrefix}-edit-${i}`}>
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => remove(r)} className="p-1 text-slate-400 hover:text-rose-600"
                              title="Sil" data-testid={`${testIdPrefix}-del-${i}`}>
                        <TrashSimple size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
        </>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {kind === "gelir" ? <TrendUp size={18} weight="duotone" className="text-emerald-600" />
                                : <TrendDown size={18} weight="duotone" className="text-rose-600" />}
              {editing ? `${kindLabel} Kaydı Düzenle` : `Yeni ${kindLabel} Kaydı`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tarih *</label>
                <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                       className="rounded-none sm:rounded-sm" data-testid="entry-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şantiye *</label>
                <Select value={form.site_id} onValueChange={(v) => set("site_id", v)}>
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="entry-site">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="font-mono text-xs text-[#1B4776] mr-2">{s.code}</span>{s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="entry-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gercek">Gerçekleşen</SelectItem>
                    <SelectItem value="tahmin">Tahmin (Plan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Kayıt Türü — Resmi / Gayri Resmi / Vergi-Harç */}
            <div className={`p-3 border-2 ${
              form.formality === "gayri_resmi"
                ? "border-amber-400 bg-amber-50"
                : form.formality === "vergi_harc"
                ? "border-sky-400 bg-sky-50"
                : "border-emerald-300 bg-emerald-50/40"
            }`}>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
                Kayıt Türü *
              </label>
              <div className={`grid ${kind === "gider" ? "grid-cols-3" : "grid-cols-2"} gap-2 mt-1.5`}>
                <button type="button"
                        onClick={() => set("formality", "resmi")}
                        className={`p-2 border-2 text-left transition-colors ${
                          form.formality === "resmi"
                            ? "border-emerald-600 bg-white"
                            : "border-slate-200 bg-white hover:border-emerald-300"
                        }`}
                        data-testid="entry-formality-resmi">
                  <div className="text-xs font-bold text-emerald-700">✓ Resmi Kayıt</div>
                  <div className="text-[10px] text-slate-600 mt-0.5">Muhasebe programında kayıtlı (giderleştirilebilen)</div>
                </button>
                <button type="button"
                        onClick={() => set("formality", "gayri_resmi")}
                        className={`p-2 border-2 text-left transition-colors ${
                          form.formality === "gayri_resmi"
                            ? "border-amber-600 bg-white"
                            : "border-slate-200 bg-white hover:border-amber-300"
                        }`}
                        data-testid="entry-formality-gayri">
                  <div className="text-xs font-bold text-amber-800">⚠ Gayri Resmi</div>
                  <div className="text-[10px] text-slate-600 mt-0.5">Muhasebeye yansımayan (elden ödeme vb.)</div>
                </button>
                {kind === "gider" && (
                  <button type="button"
                          onClick={() => set("formality", "vergi_harc")}
                          className={`p-2 border-2 text-left transition-colors ${
                            form.formality === "vergi_harc"
                              ? "border-sky-600 bg-white"
                              : "border-slate-200 bg-white hover:border-sky-300"
                          }`}
                          data-testid="entry-formality-vergi-harc">
                    <div className="text-xs font-bold text-sky-800">⚖ Vergi / Harç</div>
                    <div className="text-[10px] text-slate-600 mt-0.5">Vergi, harç ve resmî ödemeler (KDV'siz tek tutar)</div>
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kategori * (Maliyet Kırınım Yapısı)</label>
              <EntryCategoryCombobox
                value={form.category_code}
                onChange={(code) => set("category_code", code)}
                options={leafCategories}
                testId="entry-category"
              />
            </div>

            {form.formality === "resmi" ? (
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Tutar Ayrıntılı
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      KDV Hariç ₺
                    </div>
                    <MoneyInput value={form.amount_no_vat}
                           onChange={onChangeNoVat}
                           placeholder="0"
                           className="rounded-none sm:rounded-sm font-mono text-right font-bold"
                           data-testid="entry-amount-no-vat" />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      KDV Bedeli ₺
                    </div>
                    <MoneyInput value={form.vat_amount}
                           onChange={onChangeVatAmount}
                           placeholder="0"
                           className="rounded-none sm:rounded-sm font-mono text-right font-bold"
                           data-testid="entry-vat-amount" />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      Diğer Vergiler ₺
                    </div>
                    <MoneyInput value={form.other_taxes}
                           onChange={onChangeOtherTaxes}
                           placeholder="0"
                           className="rounded-none sm:rounded-sm font-mono text-right font-bold"
                           data-testid="entry-other-taxes" />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      KDV Dahil (Toplam) ₺
                    </div>
                    <MoneyInput value={form.amount_with_vat}
                           onChange={onChangeWithVat}
                           placeholder="0"
                           className="rounded-none sm:rounded-sm font-mono text-right font-bold"
                           data-testid="entry-amount-with-vat" />
                  </div>
                </div>
                <div className="text-[10px] text-slate-500">
                  KDV Hariç + KDV Bedeli + Diğer Vergiler = Toplam. Alt üç alanı yazınca toplam otomatik hesaplanır; toplam alanını manuel de düzeltebilirsiniz.
                </div>
              </div>
            ) : (
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tutar ₺</label>
                <MoneyInput value={form.amount_no_vat}
                       onChange={onChangeNoVat}
                       placeholder="0"
                       className="rounded-none sm:rounded-sm font-mono text-right font-bold text-lg" data-testid="entry-amount" />
                <div className="text-[10px] text-slate-400 mt-1">
                  {form.formality === "vergi_harc"
                    ? "Vergi / harç kaydı — KDV'siz tek tutar."
                    : `Gayri resmi ${kindLabel.toLowerCase()} kaydı — KDV hariç net tutar.`}
                </div>
              </div>
            )}

            {/* Tedarikçi / Firma / Ekip seçici */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Tedarikçi / Firma / Ekip
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Select
                  value={form.party_type || "none"}
                  onValueChange={(v) => {
                    const t = v === "none" ? "" : v;
                    setForm((f) => ({ ...f, party_type: t, party_id: "", party_name: "" }));
                  }}
                >
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="entry-party-type">
                    <SelectValue placeholder="Tür Seç" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yok —</SelectItem>
                    <SelectItem value="supplier">Tedarikçi</SelectItem>
                    <SelectItem value="sub_firm">Alt Yüklenici / Firma</SelectItem>
                    <SelectItem value="team">Ekip</SelectItem>
                    <SelectItem value="free">Serbest Metin</SelectItem>
                  </SelectContent>
                </Select>
                {form.party_type === "supplier" ? (
                  <Select
                    value={form.party_id || "none"}
                    onValueChange={(v) => {
                      const id = v === "none" ? "" : v;
                      const sup = suppliers.find((s) => s.id === id);
                      setForm((f) => ({
                        ...f, party_id: id,
                        party_name: sup?.name || "",
                      }));
                    }}
                  >
                    <SelectTrigger className="sm:col-span-2 rounded-none sm:rounded-sm" data-testid="entry-party-supplier">
                      <SelectValue placeholder="Tedarikçi seç..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Yok —</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : form.party_type === "sub_firm" ? (
                  <Select
                    value={form.party_id || "none"}
                    onValueChange={(v) => {
                      const id = v === "none" ? "" : v;
                      const sf = subFirms.find((s) => s.id === id);
                      setForm((f) => ({
                        ...f, party_id: id,
                        party_name: sf?.name || "",
                      }));
                    }}
                  >
                    <SelectTrigger className="sm:col-span-2 rounded-none sm:rounded-sm" data-testid="entry-party-subfirm">
                      <SelectValue placeholder="Firma/Ekip (Alt Yüklenici) seç..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Yok —</SelectItem>
                      {subFirms.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : form.party_type === "team" ? (
                  <Select
                    value={form.party_id || "none"}
                    onValueChange={(v) => {
                      const id = v === "none" ? "" : v;
                      const tm = teams.find((t) => t.id === id);
                      setForm((f) => ({
                        ...f, party_id: id,
                        party_name: tm?.name || "",
                      }));
                    }}
                  >
                    <SelectTrigger className="sm:col-span-2 rounded-none sm:rounded-sm" data-testid="entry-party-team">
                      <SelectValue placeholder="Ekip seç..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Yok —</SelectItem>
                      {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : form.party_type === "free" ? (
                  <Input
                    value={form.party_name}
                    onChange={(e) => setForm((f) => ({ ...f, party_name: e.target.value }))}
                    placeholder="Serbest metin — kayıtsız firma/kişi adı"
                    className="sm:col-span-2 rounded-none sm:rounded-sm"
                    data-testid="entry-party-free"
                  />
                ) : (
                  <div className="sm:col-span-2 text-[10px] text-slate-400 italic self-center pl-3">
                    Türü seçtikten sonra listeden bir kayıt seçin veya serbest metin girin.
                  </div>
                )}
              </div>
            </div>

            {((form.formality || "resmi") === "resmi") && (
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Fatura No</label>
                <Input value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)}
                       placeholder="Örn: 2026-000123"
                       className="rounded-none sm:rounded-sm font-mono" data-testid="entry-invoice" />
                <div className="text-[10px] text-slate-400 mt-1">
                  Resmi kayıtlarda fatura numarası zorunludur.
                </div>
              </div>
            )}

            {/* Ödeme Hesabı (Faz 34.55) — hangi hesap / yöntem ile yapıldı */}
            <div className="p-3 border-2 border-slate-200 space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
                Ödeme Yapılan Hesap {kind === "gider" ? "" : "(Alıcı)"}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {PAYMENT_METHOD_OPTS.map((m) => {
                  const active = form.payment_method === m.v;
                  return (
                    <button key={m.v} type="button"
                      onClick={() => {
                        // Kanal değişince from_account'ı da temizle (yanlış hesap tipi kalmasın)
                        if (active) {
                          setForm((f) => ({ ...f, payment_method: "", from_account: "" }));
                        } else {
                          setForm((f) => ({ ...f, payment_method: m.v, from_account: "" }));
                        }
                      }}
                      className={`border-2 py-2 px-2 text-center transition-colors text-xs font-bold ${
                        active
                          ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid={`entry-method-${m.v}`}>
                      {m.long}
                    </button>
                  );
                })}
              </div>
              {/* Kanala göre alt seçici */}
              {form.payment_method === "banka" ? (
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Banka Hesabı Seçin
                  </label>
                  {bankAccounts.length === 0 ? (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                      Kayıtlı banka hesabı yok. <b>Bütçe Yönetimi → Hesaplar / Bankalar</b> sayfasından tanımlayın.
                    </div>
                  ) : (
                    <Select value={form.from_account || "none"}
                            onValueChange={(v) => set("from_account", v === "none" ? "" : v)}>
                      <SelectTrigger className="rounded-none sm:rounded-sm h-10"
                                     data-testid="entry-bank-picker">
                        <SelectValue placeholder="Banka hesabı seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Seçim yok —</SelectItem>
                        {bankAccounts.map((a) => (
                          <SelectItem key={a.id} value={`${a.bank_name}${a.iban ? " · " + a.iban : ""}`}>
                            {a.bank_name}{a.iban && <span className="font-mono text-slate-500 ml-1">· {a.iban}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ) : form.payment_method === "kredi_karti" ? (
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Kredi Kartı Seçin
                  </label>
                  {creditCards.length === 0 ? (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                      Kayıtlı kredi kartı yok. <b>Bütçe Yönetimi → Hesaplar / Bankalar</b> sayfasından tanımlayın.
                    </div>
                  ) : (
                    <Select value={form.from_account || "none"}
                            onValueChange={(v) => set("from_account", v === "none" ? "" : v)}>
                      <SelectTrigger className="rounded-none sm:rounded-sm h-10"
                                     data-testid="entry-card-picker">
                        <SelectValue placeholder="Kredi kartı seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Seçim yok —</SelectItem>
                        {creditCards.map((a) => (
                          <SelectItem key={a.id} value={`${a.label} · ${a.bank_name}${a.card_number_masked ? " · " + a.card_number_masked : ""}`}>
                            {a.label} — {a.bank_name} {a.card_number_masked && <span className="font-mono text-slate-400">· {a.card_number_masked}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ) : form.payment_method === "nakit" ? (
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kasa / Nakit Notu (opsiyonel)</label>
                  <Input value={form.from_account}
                         onChange={(e) => set("from_account", e.target.value)}
                         placeholder="Örn: Şantiye Kasa 1"
                         className="rounded-none sm:rounded-sm text-xs"
                         data-testid="entry-cash-note" />
                </div>
              ) : form.payment_method === "cek" || form.payment_method === "senet" ? (
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    {form.payment_method === "cek" ? "Çek No / Banka (opsiyonel)" : "Senet No (opsiyonel)"}
                  </label>
                  <Input value={form.from_account}
                         onChange={(e) => set("from_account", e.target.value)}
                         placeholder={form.payment_method === "cek" ? "Örn: Vakıf 12345 — 15.03.2026" : "Örn: 2026-045"}
                         className="rounded-none sm:rounded-sm text-xs"
                         data-testid="entry-cek-senet" />
                </div>
              ) : null}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Açıklama</label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)}
                     placeholder="Örn: Şubat 2026 hakediş / Çimento alımı"
                     className="rounded-none sm:rounded-sm" data-testid="entry-desc" />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar</label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                        rows={2} className="rounded-none sm:rounded-sm" data-testid="entry-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save}
                    className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                    data-testid="entry-save">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Toplu İçe Aktarma Dialog — sadece Gider modunda */}
      {kind === "gider" && (
        <Dialog open={importOpen} onOpenChange={(v) => {
          setImportOpen(v);
          if (!v) { setImportPreview(null); setImportFile(null); }
        }}>
          <DialogContent className="max-w-5xl rounded-none sm:rounded-sm max-h-[92vh] overflow-y-auto" data-testid="entry-import-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileXls size={18} weight="duotone" className="text-[#95C11F]" />
                Excel'den Toplu Gider Kaydı İçe Aktar
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Adım 1 — Şablon indir */}
              <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-slate-50">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-slate-800">1. Şablonu indirin</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Boş şablonu indirin, satırları doldurun; kolonları veya başlıkları değiştirmeyin.
                    </div>
                    <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                      <span className="font-bold text-slate-700">Zorunlu alanlar:</span>
                      {" "}Tarih · Şantiye Kodu · Kategori (CBS) · Tedarikçi/Firma/Ekip ·
                      {" "}Fatura Numarası · KDV Hariç Bedel · KDV Dahil Bedel · KDV Oranı
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => {
                    window.open(`${API}/budget/entries/import-template.xlsx?kind=gider`, "_blank");
                  }} className="rounded-none sm:rounded-sm" data-testid="import-tpl-dl">
                    <DownloadSimple size={14} weight="bold" className="mr-1" />
                    Şablonu İndir (.xlsx)
                  </Button>
                </div>
              </Card>

              {/* Adım 2 — Dosya seç ve önizle */}
              <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
                <div className="text-sm font-bold text-slate-800 mb-2">2. Doldurulmuş dosyayı yükleyin</div>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="flex-1 text-xs border border-slate-300 p-2 rounded-none sm:rounded-sm"
                    data-testid="import-file-input"
                  />
                  <Button onClick={async () => {
                    if (!importFile) { toast.error("Önce bir dosya seçin"); return; }
                    setImportBusy(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", importFile);
                      const { data } = await axios.post(
                        `${API}/budget/entries/import-preview?kind=gider`, fd,
                        { headers: { "Content-Type": "multipart/form-data" } });
                      setImportPreview(data);
                    } catch (e) { toast.error(formatErr(e)); }
                    finally { setImportBusy(false); }
                  }} disabled={!importFile || importBusy}
                     className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                     data-testid="import-preview-btn">
                    <UploadSimple size={14} weight="bold" className="mr-1" />
                    {importBusy ? "Yükleniyor..." : "Yükle & Önizle"}
                  </Button>
                </div>
              </Card>

              {/* Adım 3 — Önizleme + Onay */}
              {importPreview && (
                <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-slate-800">3. Önizleme</div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        Toplam <span className="font-bold">{importPreview.total}</span> satır;
                        <span className="font-bold text-emerald-700"> {importPreview.valid} yüklenebilir</span>
                        {importPreview.warnings > 0 && (
                          <>, <span className="font-bold text-amber-700">{importPreview.warnings} uyarılı</span></>
                        )}
                        {importPreview.errors > 0 && (
                          <>, <span className="font-bold text-rose-700">{importPreview.errors} bloklandı</span></>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        Uyarılı satırlar da yüklenir; ilgili kayıtta uyarı işareti görünür.
                      </div>
                    </div>
                    <Button onClick={async () => {
                      try {
                        const { data } = await axios.post(
                          `${API}/budget/entries/import-commit`,
                          { rows: importPreview.rows });
                        const msg = data.warnings
                          ? `${data.created} kayıt oluşturuldu (${data.warnings} tanesi uyarılı)`
                          : `${data.created} gider kaydı oluşturuldu`;
                        toast.success(msg);
                        setImportOpen(false);
                        setImportPreview(null); setImportFile(null);
                        load();
                      } catch (e) { toast.error(formatErr(e)); }
                    }} disabled={importPreview.valid === 0}
                       className="bg-[#95C11F] hover:bg-[#7ea01a] text-white rounded-none sm:rounded-sm"
                       data-testid="import-commit-btn">
                      <CheckCircle size={14} weight="bold" className="mr-1" />
                      {importPreview.valid} Kaydı Yükle
                    </Button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-slate-100">
                        <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                          <th className="px-2 py-1.5 text-left w-10">#</th>
                          <th className="px-2 py-1.5 text-left w-24">Tarih</th>
                          <th className="px-2 py-1.5 text-left w-24">Şantiye</th>
                          <th className="px-2 py-1.5 text-left w-32">Kategori</th>
                          <th className="px-2 py-1.5 text-left w-40">Tedarikçi/Firma/Ekip</th>
                          <th className="px-2 py-1.5 text-left w-28">Fatura No</th>
                          <th className="px-2 py-1.5 text-right w-24">KDV Hariç</th>
                          <th className="px-2 py-1.5 text-right w-24">KDV Dahil</th>
                          <th className="px-2 py-1.5 text-right w-16">KDV %</th>
                          <th className="px-2 py-1.5 text-right w-20">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.rows.map((r, i) => {
                          const hasWarn = (r.warnings || []).length > 0;
                          const hasErr = !r.valid;
                          const rowCls = hasErr
                            ? "bg-rose-50"
                            : hasWarn
                            ? "bg-amber-50/70"
                            : "";
                          return (
                          <tr key={i} className={`border-b border-slate-100 ${rowCls}`}
                              data-testid={`import-row-${r.row_no}`}>
                            <td className="px-2 py-1.5 font-mono text-slate-500">{r.row_no}</td>
                            <td className="px-2 py-1.5">{r.date || "—"}</td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-[10px] font-bold text-[#1B4776]">{r.site_code}</div>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-[10px]">{r.category_code || "—"}</div>
                              {r.category_name && <div className="text-[9px] text-slate-400">{r.category_name}</div>}
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="text-[11px] text-slate-800">{r.party_name || "—"}</div>
                              {r.party_type && (
                                <div className="text-[9px] text-slate-400 uppercase tracking-wider">
                                  {r.party_type === "supplier" ? "Tedarikçi" : r.party_type === "sub_firm" ? "Alt Yüklenici" : r.party_type === "team" ? "Ekip" : ""}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{r.invoice_no || "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {(r.amount_no_vat || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {(r.amount_with_vat || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">{r.kdv_rate ?? 0}</td>
                            <td className="px-2 py-1.5 text-right">
                              {hasErr ? (
                                <span title={(r.errors || []).join("; ")}
                                      className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-rose-700">
                                  <WarningCircle size={10} weight="fill" /> Bloklandı
                                </span>
                              ) : hasWarn ? (
                                <span title={(r.warnings || []).join("; ")}
                                      className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-amber-700">
                                  <WarningCircle size={10} weight="fill" /> Uyarı
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-emerald-700">
                                  <CheckCircle size={10} weight="fill" /> Geçerli
                                </span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Bloklanan satırlar */}
                  {importPreview.errors > 0 && (
                    <div className="p-3 border-t border-slate-200 bg-rose-50/50 max-h-32 overflow-y-auto">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 mb-1">
                        Bloklanan Satırlar (Yüklenmez — Şantiye eksik/geçersiz)
                      </div>
                      {importPreview.rows.filter((r) => !r.valid).map((r, i) => (
                        <div key={i} className="text-[11px] text-rose-700">
                          <span className="font-mono">#{r.row_no}</span>: {r.errors.join(" · ")}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Uyarılı satırlar */}
                  {importPreview.warnings > 0 && (
                    <div className="p-3 border-t border-slate-200 bg-amber-50/50 max-h-32 overflow-y-auto">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">
                        Uyarılı Satırlar (Yüklenir — kayıtta uyarı işareti çıkar)
                      </div>
                      {importPreview.rows.filter((r) => r.valid && (r.warnings || []).length).map((r, i) => (
                        <div key={i} className="text-[11px] text-amber-700">
                          <span className="font-mono">#{r.row_no}</span>: {(r.warnings || []).join(" · ")}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)}
                      className="rounded-none sm:rounded-sm">Kapat</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Shell>
  );
}


// ============================================================================
// Şantiye Genel Bakış — kart görünümü (gelir sayfası landing)
// ============================================================================
function SiteLandingView({ summary, onSelectSite, testIdPrefix }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let out = summary || [];
    if (statusFilter !== "all") {
      out = out.filter((s) => s.status === statusFilter);
    }
    if (q) {
      const nq = q.toLowerCase();
      out = out.filter((s) =>
        s.code?.toLowerCase().includes(nq) ||
        s.name?.toLowerCase().includes(nq) ||
        s.city?.toLowerCase().includes(nq)
      );
    }
    return out;
  }, [summary, statusFilter, q]);

  const totals = useMemo(() => ({
    gelirTotal: (summary || []).reduce((a, s) => a + (s.gelir_total || 0), 0),
    giderTotal: (summary || []).reduce((a, s) => a + (s.gider_total || 0), 0),
    net: (summary || []).reduce((a, s) => a + (s.net || 0), 0),
    aktif: (summary || []).filter((s) => s.status === "aktif").length,
    kapali: (summary || []).filter((s) => s.status === "kapali").length,
  }), [summary]);

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam Şantiye</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{summary?.length || 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            <span className="text-emerald-700 font-semibold">{totals.aktif} aktif</span>
            {" • "}
            <span className="text-slate-500 font-semibold">{totals.kapali} kapalı</span>
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-emerald-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Toplam Gelir</div>
          <div className="text-lg font-bold text-emerald-700 mt-1 font-mono">{fmtTL(totals.gelirTotal)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-rose-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold">Toplam Gider</div>
          <div className="text-lg font-bold text-rose-700 mt-1 font-mono">{fmtTL(totals.giderTotal)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-blue-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1B4776] font-bold">Net (Gelir − Gider)</div>
          <div className={`text-lg font-bold mt-1 font-mono ${totals.net >= 0 ? "text-[#1B4776]" : "text-rose-700"}`}>
            {fmtTL(totals.net)}
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-slate-50/60">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold">Bilgi</div>
          <div className="text-[10px] text-slate-500 mt-1 leading-tight">
            Şantiye kartına tıklayarak gelir/gider kayıtlarına ulaşabilirsiniz. Kapalı şantiyeler de dahildir.
          </div>
        </Card>
      </div>

      {/* Filtreler */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Kod, isim, şehir ara..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 rounded-none sm:rounded-sm"
                   data-testid={`${testIdPrefix}-site-search`} />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 rounded-none sm:rounded-sm"
                            data-testid={`${testIdPrefix}-site-status-filter`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Aktif + Kapalı</SelectItem>
              <SelectItem value="aktif">Sadece Aktif</SelectItem>
              <SelectItem value="kapali">Sadece Kapalı</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Site Kartları */}
      {filtered.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm border-slate-200 text-center text-slate-400">
          <Buildings size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm">Kriterlere uyan şantiye bulunamadı.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((s) => {
            const isKapali = s.status === "kapali";
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelectSite(s.id)}
                data-testid={`${testIdPrefix}-site-card-${s.code || s.id}`}
                className={`text-left rounded-none sm:rounded-sm border-2 p-0 transition-all hover:shadow-lg group ${
                  isKapali
                    ? "border-slate-300 bg-slate-50 opacity-80 hover:opacity-100"
                    : "border-slate-200 bg-white hover:border-[#1B4776]"
                }`}
              >
                {/* Header */}
                <div className={`px-4 py-3 border-b-2 ${
                  isKapali ? "bg-slate-100 border-slate-300" : "bg-gradient-to-r from-[#1B4776] to-[#12324f] border-[#95C11F]"
                }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className={`font-mono text-[10px] font-bold ${isKapali ? "text-slate-500" : "text-[#95C11F]"}`}>
                        {s.code}
                      </div>
                      <div className={`text-sm font-bold mt-0.5 ${isKapali ? "text-slate-700" : "text-white"}`}>
                        {s.name}
                      </div>
                    </div>
                    <span className={`shrink-0 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                      isKapali
                        ? "bg-slate-200 text-slate-600 border-slate-400"
                        : "bg-emerald-500/20 text-emerald-100 border-emerald-300"
                    }`}>
                      {isKapali ? "Kapalı" : "Aktif"}
                    </span>
                  </div>
                  {(s.city || s.project_manager) && (
                    <div className={`text-[10px] mt-1 flex items-center gap-2 ${
                      isKapali ? "text-slate-500" : "text-slate-200"
                    }`}>
                      {s.city && <span className="inline-flex items-center gap-0.5"><MapPin size={10} weight="bold" /> {s.city}</span>}
                      {s.project_manager && <span>• {s.project_manager}</span>}
                    </div>
                  )}
                </div>

                {/* Body — Rakamlar */}
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs">
                      <TrendUp size={12} weight="bold" className="text-emerald-600" />
                      <span className="text-slate-600 font-semibold">Gelir</span>
                      <span className="text-[10px] text-slate-400">({s.gelir_count} kayıt)</span>
                    </div>
                    <div className="font-mono text-sm font-bold text-emerald-700">
                      {fmtTL(s.gelir_total)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs">
                      <TrendDown size={12} weight="bold" className="text-rose-600" />
                      <span className="text-slate-600 font-semibold">Gider</span>
                      <span className="text-[10px] text-slate-400">({s.gider_count} kayıt)</span>
                    </div>
                    <div className="font-mono text-sm font-bold text-rose-700">
                      {fmtTL(s.gider_total)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Net</span>
                    <div className={`font-mono text-base font-bold ${s.net >= 0 ? "text-[#1B4776]" : "text-rose-700"}`}>
                      {fmtTL(s.net)}
                    </div>
                  </div>
                </div>

                {/* Footer — CTA */}
                <div className={`px-4 py-2 border-t flex items-center justify-between text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  isKapali
                    ? "bg-slate-100 text-slate-500 border-slate-200 group-hover:bg-slate-200"
                    : "bg-[#F7FBE9] text-[#556B10] border-[#95C11F]/40 group-hover:bg-[#95C11F] group-hover:text-white"
                }`}>
                  <span>Kayıtları Aç</span>
                  <CaretRight size={12} weight="bold" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
